import sortBy from "lodash/sortBy";
import { Redirect } from "react-router-dom";
import { MemberSetModel } from "@app/members";
import { TemplateSpecialtyGroup } from "@app/templates";
import WrappedCreateMemberTable from "@app/projects/create/CreateMemberTable";
import CreateProject from "@app/projects/create/createProject";
import { SpecialtyOverrides } from "@app/projects/create/interfaces";
import WrappedMembershipBindingTable from "@app/projects/create/MembershipBindingTable";
import WizardStep from "@app/projects/create/WizardStep";
import { FC, useEffect, useState } from "react";

interface CreateProjectStepMembersProps {
  alternateMemberships: MemberSetModel[];
  baseUrl: string;
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  deleteAltMembershipRow: (memberPop: number) => void;
  disableMemberBindingTable: boolean;
  generateAlternateMemberships: () => void;
  handleMemberPopAliasChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopAliasChange2: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMemberPopChange2: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePrimaryMembershipBindingChange: (memberPop: number, code: string) => void;
  handleSecondaryMembershipBindingChange: (memberPop: number, code: string) => void;
  handleRiskPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  isZipProject: boolean;
  onNextClick: () => void;
  progressList: string[];
  projectMemberPopulations: MemberSetModel[];
  specialtyGroups: TemplateSpecialtyGroup[];
  specialtyOverrides: SpecialtyOverrides[];
  setPrimarySpecialtyOverride: (memberPop: number, code: string) => void;
  setSecondarySpecialtyOverride: (memberPop: number, code: string) => void;

  handleAlternativeMemberPopAliasChange: (alias: string, memberPopId: number) => void;
  handleAlternativeMemberPopChange: (memberPopId: number, prevMemberPopId: number) => void;
  handleNewAlternativeMemberPop: () => void;
  currentIndex: number;
}

const sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];
const CreateProjectStepMembers: FC<CreateProjectStepMembersProps> = (props: CreateProjectStepMembersProps) => {
  const [restartApp] = useState(!props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step));
  const [sortedSpecialtyGroups, setSortedSpecialtyGroups] = useState(props.specialtyGroups && sortBy(props.specialtyGroups, sortOrder) || props.specialtyGroups);

  useEffect(() => {
    setSortedSpecialtyGroups(props.specialtyGroups && sortBy(props.specialtyGroups, sortOrder) || props.specialtyGroups)
  }, [props.specialtyGroups]);

  if (restartApp) {
    return <Redirect to={`${props.baseUrl}/1`} />;
  }

  const handleIsNextDisabled = () => {
    const adequacyTemplateComplete = (!!props.createProjectModel.adequacyTemplateCollectionId &&
      !!props.createProjectModel.adequacyTemplateCorrelationId &&
      props.createProjectModel.adequacyTemplateCorrelationId !== "");
    const accuracyTemplateComplete = (!!props.createProjectModel.accuracyTemplateCollectionId &&
      !!props.createProjectModel.accuracyTemplateCorrelationId &&
      props.createProjectModel.accuracyTemplateCorrelationId !== "");
    const memberSetIdSelected = !!props.createProjectModel.memberSetId;
    const memberSetsWithAliases = props.createProjectModel.createProjectOverrideMemberSets.filter((oms) => !!oms.alias);
    const memberSetAliases = new Set(memberSetsWithAliases.map((oms) => oms.alias));
    return ((!accuracyTemplateComplete || adequacyTemplateComplete) && !memberSetIdSelected)
      || (!adequacyTemplateComplete && !accuracyTemplateComplete)
      || (adequacyTemplateComplete && memberSetsWithAliases.length !== memberSetAliases.size);
  }

  return (
    <WizardStep heading="Create a New Project" subheading="Define Analysis :: Members" baseUrl={props.baseUrl} prevUrl={(props.currentIndex - 1).toString()} nextUrl={(props.currentIndex + 1).toString()} progressList={props.progressList} isNextDisabled={handleIsNextDisabled()} onNextClick={props.onNextClick}>
      {props.projectMemberPopulations && props.projectMemberPopulations.length > 0 &&
        <div className="form-group row">
          <div className="col-11">
            <WrappedCreateMemberTable
              createProjectModel={props.createProjectModel}
              deleteAltMembershipRow={props.deleteAltMembershipRow}
              generateAlternateMemberships={props.generateAlternateMemberships}
              handleAlternativeMemberPopAliasChange={props.handleAlternativeMemberPopAliasChange}
              handleAlternativeMemberPopChange={props.handleAlternativeMemberPopChange}
              handleNewAlternativeMemberPop={props.handleNewAlternativeMemberPop}
              handleMemberPopAliasChange={props.handleMemberPopAliasChange}
              handleMemberPopAliasChange2={props.handleMemberPopAliasChange2}
              handleMemberPopChange={props.handleMemberPopChange}
              handleMemberPopChange2={props.handleMemberPopChange2}
              handleRiskPopChange={props.handleRiskPopChange}
              isZipProject={props.isZipProject}
              projectMemberPopulations={props.projectMemberPopulations}
            />
          </div>
        </div>
      }
      {sortedSpecialtyGroups &&
        sortedSpecialtyGroups.length > 0 &&
        !props.disableMemberBindingTable &&
        (
          <div className="form-group row">
            <div className="col-11">
              <WrappedMembershipBindingTable
                alternateMemberships={props.alternateMemberships}
                canEdit={true}
                createProjectModel={props.createProjectModel}
                excludedSpecialtyGroupCodes={props.createProjectModel.excludedSpecialtyGroupCodes}
                specialtyGroups={props.specialtyGroups}
                uniqueKey={`${props.createProjectModel.adequacyTemplateCollectionId}${props.createProjectModel.adequacyTemplateCorrelationId}`}
                projectMemberPopulations={props.projectMemberPopulations}
                handlePrimaryMembershipBindingChange={props.handlePrimaryMembershipBindingChange}
                handleSecondaryMembershipBindingChange={props.handleSecondaryMembershipBindingChange}
                setPrimarySpecialtyOverride={props.setPrimarySpecialtyOverride}
                setSecondarySpecialtyOverride={props.setSecondarySpecialtyOverride}
                specialtyOverrides={props.specialtyOverrides}
              />
            </div>
          </div>
        )}
    </WizardStep>
  );
}

export default CreateProjectStepMembers;
